import { Suspense, useState, useRef, useCallback, useEffect } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { CameraControls } from '@react-three/drei';
import { NoToneMapping, PerspectiveCamera as ThreePerspCamera, Color } from 'three';
import { AreaConfig } from '#server/models';
import { useAdminSubjectDraft, LabelDraft } from '@/contexts';
import { AdminSubject } from '../components/AdminSubject';
import { AdminContextPortal } from '../components/AdminContextPreview';
import { Loader, NavigationControlsEventListener } from '@/components';

interface Step6Props {
  areaConfig: AreaConfig;
}

/**
 * Lives inside the Canvas so it can access the WebGL renderer and already-loaded
 * scene via useThree. On capture, it:
 *  1. Temporarily sets a solid #4b4b4b background on the main scene
 *  2. Renders using the current camera view (aspect temporarily forced to 1 so
 *     the square crop is undistorted)
 *  3. Center-crops to a square and scales to 300×300 JPEG
 *  4. Triggers a browser download
 */
const ThumbnailCapture = ({
  captureRef,
  slug,
}: {
  captureRef: React.MutableRefObject<(() => void) | null>;
  slug: string;
}) => {
  // Use selectors so this component only re-renders when these stable references
  // are replaced (i.e. essentially never), not on every store update.
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);
  const camera = useThree((s) => s.camera);

  useEffect(() => {
    captureRef.current = () => {
    // Temporarily override scene background to solid gray
    const prevBg = scene.background;
    scene.background = new Color('#4b4b4b');

    // Force aspect=1 on the current camera so rendering into the square viewport
    // is undistorted. Save and restore original aspect afterwards.
    const perspCam = camera as ThreePerspCamera;
    const prevAspect = perspCam.aspect;
    perspCam.aspect = 1;
    perspCam.updateProjectionMatrix();

    // Compute a centered square in the canvas.
    // sy is identical in WebGL coords (y-from-bottom) and CSS/2D coords (y-from-top)
    // because the square is centred: (H - side) / 2 satisfies H - sy - side = sy.
    const src = gl.domElement;
    const side = Math.min(src.width, src.height);
    const sx = Math.round((src.width - side) / 2);
    const sy = Math.round((src.height - side) / 2);

    // Render into the square viewport. AdminContextPortal's useFrame leaves a small
    // portal viewport set, so we always reset it here explicitly.
    const prevAutoClear = gl.autoClear;
    gl.autoClear = true;
    gl.setScissorTest(false);
    gl.setViewport(sx, sy, side, side);
    gl.render(scene, camera);

    // Copy exactly the square viewport region to the 300×300 output canvas
    const dst = document.createElement('canvas');
    dst.width = 300;
    dst.height = 300;
    const ctx = dst.getContext('2d')!;
    ctx.drawImage(src, sx, sy, side, side, 0, 0, 300, 300);

    // Restore camera aspect and re-render the normal scene so nothing persists.
    perspCam.aspect = prevAspect;
    perspCam.updateProjectionMatrix();
    scene.background = prevBg;
    gl.setViewport(0, 0, src.width, src.height);
    gl.render(scene, camera);
    gl.autoClear = prevAutoClear;

    const link = document.createElement('a');
    link.href = dst.toDataURL('image/jpeg', 0.92);
    link.download = `${slug}-thumbnail.jpg`;
    link.click();
    };
    return () => { captureRef.current = null; };
  }, [gl, scene, camera, slug, captureRef]);

  return null;
};

export const Step6Preview = ({ areaConfig }: Step6Props) => {
  const { draft } = useAdminSubjectDraft();
  const { config3D } = draft;
  const [forceVisible, setForceVisible] = useState(false);
  const [activeLabelId, setActiveLabelId] = useState<string | null>(null);
  const [showCropGuide, setShowCropGuide] = useState(true);
  const [cropGuideStyle, setCropGuideStyle] = useState<React.CSSProperties>({});
  const cameraControlsRef = useRef<CameraControls | null>(null);
  const captureRef = useRef<(() => void) | null>(null);
  const cropRoRef = useRef<ResizeObserver | null>(null);

  // Callback ref: fires when the canvas container mounts/unmounts, even though
  // it's conditionally rendered. A plain useRef+useEffect([]) would miss it if
  // the element isn't in the DOM on the first render.
  const canvasContainerRef = useCallback((el: HTMLDivElement | null) => {
    cropRoRef.current?.disconnect();
    cropRoRef.current = null;
    if (!el) return;
    const update = () => {
      const { width, height } = el.getBoundingClientRect();
      const side = Math.min(width, height) - 2; // inset 1px each side so the border isn't clipped by overflow-hidden
      setCropGuideStyle({ width: side, height: side, left: (width - side) / 2, top: (height - side) / 2 });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    cropRoRef.current = ro;
  }, []);

  const cameraControlsCallback = useCallback((controls: CameraControls | null) => {
    cameraControlsRef.current = controls;
    if (controls) {
      controls.mouseButtons.right = 8;
      controls.touches.two = 16384;
      controls.touches.three = 512;
    }
  }, []);

  const resetCamera = () => cameraControlsRef.current?.reset(true);

  const flyToLabel = (label: LabelDraft) => {
    const controls = cameraControlsRef.current;
    if (!controls) return;
    const { camera } = label.config3D;
    controls.setPosition(camera.position[0], camera.position[1], camera.position[2], true);
    controls.dollyTo(camera.distance, true);
    controls.setFocalOffset(camera.focalOffset[0], camera.focalOffset[1], camera.focalOffset[2], true);
    controls.rotateTo(camera.rotation.azimuthAngle, camera.rotation.polarAngle, true);
    controls.normalizeRotations();
  };

  const handleLabelClick = (labelId: string) => {
    if (activeLabelId === labelId) {
      setActiveLabelId(null);
      return;
    }
    setActiveLabelId(labelId);
    const label = draft.labels.find((l) => l._id === labelId);
    if (label) flyToLabel(label);
  };

  const hasSubject = !!config3D._modelBlobUrl;
  const hasContext = !!config3D._referenceContextBlobUrl || !!config3D._contextBlobUrl;

  return (
    <div className="flex flex-col gap-3" style={{ height: 'calc(100vh - 280px)' }}>
      <div className="flex items-center justify-between flex-shrink-0">
        <h2 className="text-base font-semibold text-[#E8E8E8]">Step 6 – Preview</h2>
        {hasSubject && (
          <div className="flex items-center gap-3">
            {draft.labels.length > 0 && (
              <label className="flex items-center gap-1.5 text-xs text-[#999] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={forceVisible}
                  onChange={(e) => setForceVisible(e.target.checked)}
                  className="accent-[#C3D82E]"
                />
                Show all labels
              </label>
            )}
            <label className="flex items-center gap-1.5 text-xs text-[#999] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showCropGuide}
                onChange={(e) => setShowCropGuide(e.target.checked)}
                className="accent-[#C3D82E]"
              />
              Thumbnail guide
            </label>
          </div>
        )}
      </div>

      {!hasSubject ? (
        <div className="flex-1 flex items-center justify-center text-[#666] text-sm rounded border border-[#333]">
          Upload a subject FBX in Step 2 to preview.
        </div>
      ) : (
        <>
        <div
          ref={canvasContainerRef}
          className="flex-1 rounded border border-[#333] overflow-hidden relative"
          style={{ background: 'radial-gradient(transparent 20%, rgb(8, 8, 8)), rgb(88, 88, 88)' }}
          onDoubleClick={resetCamera}
        >
          {showCropGuide && (
            <div
              className="absolute pointer-events-none z-10"
              style={{
                ...cropGuideStyle,
                border: '2px dashed rgba(255,255,255,0.35)',
                boxSizing: 'border-box',
              }}
            />
          )}
          <button
            onClick={(e) => { e.stopPropagation(); resetCamera(); }}
            onDoubleClick={(e) => e.stopPropagation()}
            className="absolute top-2 right-2 z-10 px-2 py-1 rounded text-xs bg-black/50 text-[#CCC] hover:bg-black/70 hover:text-white transition-colors select-none"
            title="Reset view (or double-click)"
          >
            ↺ Reset
          </button>

          <Canvas
            frameloop="always"
            gl={{ precision: 'highp', toneMapping: NoToneMapping, autoClear: false }}
            dpr={window.devicePixelRatio}
            camera={{ position: [0, 0, config3D.dist], fov: 45, near: 0.1, far: 10000 }}
          >
            <CameraControls
              makeDefault
              ref={cameraControlsCallback}
              draggingSmoothTime={0.3}
              distance={config3D.dist}
              minDistance={config3D.distMin}
              maxDistance={config3D.distMax}
            />
            <NavigationControlsEventListener resetViewHandler={resetCamera} cameraControlsRef={cameraControlsRef} />
            <Suspense fallback={<Loader />}>
              <AdminSubject
                draft={draft}
                areaConfig={areaConfig}
                labels={draft.labels}
                showAxes={config3D.axes}
                forceVisible={forceVisible}
                activeLabelId={activeLabelId}
                onLabelClick={handleLabelClick}
              />
              {hasContext && <AdminContextPortal draft={draft} />}
            </Suspense>
            <ThumbnailCapture captureRef={captureRef} slug={draft.slug} />
          </Canvas>
        </div>
        <div className="flex justify-center flex-shrink-0">
          <button
            onClick={() => captureRef.current?.()}
            className="px-4 py-2 rounded text-sm font-semibold bg-[#C3D82E] text-[#1A1A1A] hover:bg-[#D4E845] transition-colors select-none"
          >
            ↓ Generate thumbnail
          </button>
        </div>
        </>
      )}
    </div>
  );
};
